import { useId } from "react";
import {
  REPORT_KPI_GROUPS,
  REPORT_KPI_UNITS,
  reportDelta,
  reportKpiValue,
  reportMinutesOf,
  reportTargetStatus,
  reportTargetWord,
  type ReportKpiGroupId,
  type ReportKpiId,
  type ReportMetrics,
  type ReportTargets,
} from "@gioia365/domain/report-kpis";
import { Icon } from "../design/components/Icon";
import { formatCount } from "../i18n/format";
import { useLocale, useT } from "../i18n/locale";
import { deltaHiddenText, deltaText, formatKpi, KPI_GROUP_TITLES, KPI_TEXTS, targetOf, targetText, targetWordText } from "./report-format";

// Il sommario di «Commerciali» (piano M7, Task 3; mock-up `summarySection`, `deltaBadge`, `targetBadge`): i quattro gruppi del foglio
// «KPI SALES INDIVIDUALE» (SEZ-2) per la somma delle persone scelte, ogni numero con la sua formula detta a parole, la freccia del
// confronto con il periodo prima (alla stessa ora, SEZ-16) e, dove c'è, l'obiettivo con la parola e il colore. Sotto «Chiamate effettuate»
// le chiuse presto (TEL-10) e quelle inserite a mano, sotto i minuti le tre parti. La nota a piè dice da dove vengono gli obiettivi: i
// decisi e quelli di «Calcolo Sales», che si cambiano in Impostazioni → «Obiettivi» (Task 6).

type Props = { title: string; current: ReportMetrics; previous: ReportMetrics; targets: ReportTargets };

export function SalesSummary({ title, current, previous, targets }: Props) {
  const t = useT();
  const titleId = useId();
  return (
    <section className="g-report-section" aria-labelledby={titleId}>
      <div className="g-report-head">
        <div>
          <h2 id={titleId}>{title}</h2>
          <p className="g-report-hint">{t("reports.summary.intro")}</p>
        </div>
      </div>
      <div className="g-report-groups">
        {REPORT_KPI_GROUPS.map((group) => (
          <KpiGroup key={group.id} id={group.id} kpis={group.kpis} current={current} previous={previous} targets={targets} />
        ))}
      </div>
      <p className="g-report-footnote">{t("reports.summary.footnote")}</p>
    </section>
  );
}

type GroupProps = { id: ReportKpiGroupId; kpis: readonly ReportKpiId[]; current: ReportMetrics; previous: ReportMetrics; targets: ReportTargets };

function KpiGroup({ id, kpis, current, previous, targets }: GroupProps) {
  const t = useT();
  const titleId = useId();
  return (
    <article className="g-report-group" aria-labelledby={titleId}>
      <h3 id={titleId}>{t(KPI_GROUP_TITLES[id])}</h3>
      <dl className="g-report-kpis">
        {kpis.map((kpi) => (
          <KpiLine key={kpi} id={kpi} current={current} previous={previous} targets={targets} />
        ))}
      </dl>
    </article>
  );
}

type LineProps = { id: ReportKpiId; current: ReportMetrics; previous: ReportMetrics; targets: ReportTargets };

/** Una riga del sommario: il nome con la spiegazione, il numero, la freccia e l'obiettivo. */
function KpiLine({ id, current, previous, targets }: LineProps) {
  const t = useT();
  const { locale } = useLocale();
  const unit = REPORT_KPI_UNITS[id];
  const value = reportKpiValue(id, current);
  const target = targetOf(targets, id);
  const status = reportTargetStatus(value, target);
  const texts = KPI_TEXTS[id];
  let sub: string | null = texts.how ? t(texts.how) : null;
  if (id === "calls") {
    sub = t("reports.summary.callsSub", { count: formatCount(locale, current.closedEarly) });
    if (current.manualCalls > 0) sub += t("reports.summary.callsManual", { count: formatCount(locale, current.manualCalls) });
  } else if ((id === "discoBooked" && current.discoFromClose > 0) || (id === "demoBooked" && current.demoFromClose > 0)) {
    // Le storiche entrate da Close (22/10/2026) si distinguono: già contate, a chi le ha prenotate, al giorno della prenotazione.
    sub = `${sub ?? ""}${t("reports.summary.fromClose", { count: formatCount(locale, id === "discoBooked" ? current.discoFromClose : current.demoFromClose) })}`;
  } else if (id === "minutes") {
    const minutes = reportMinutesOf(current);
    sub = t("reports.summary.minutesSub", { calls: formatKpi(locale, "minutes", minutes.calls), disco: formatKpi(locale, "minutes", minutes.disco), demo: formatKpi(locale, "minutes", minutes.demo) });
  }
  return (
    <div className="g-report-kpi">
      <dt>
        <span className="g-report-kpi__label">{t(texts.label)}</span>
        {sub ? <span className="g-report-kpi__sub">{sub}</span> : null}
      </dt>
      <dd>
        <strong className="g-report-kpi__value">{formatKpi(locale, unit, value)}</strong>
        <DeltaBadge id={id} current={current} previous={previous} />
        {target !== undefined && value !== null && status !== null ? (
          <span className="g-report-target" data-status={status}>
            {t("reports.target.badge", { target: targetText(t, locale, id, target), word: targetWordText(t, reportTargetWord(value, target)) })}
          </span>
        ) : null}
      </dd>
    </div>
  );
}

/** La freccia del confronto: in su, in giù o piatta, con il testo e, nascosto, il numero di prima (anche per «Campagne»). */
export function DeltaBadge({ id, current, previous }: { id: ReportKpiId; current: ReportMetrics; previous: ReportMetrics }) {
  const t = useT();
  const { locale } = useLocale();
  const delta = reportDelta(id, current, previous);
  if (delta === null) return null;
  const unit = REPORT_KPI_UNITS[id];
  const before = reportKpiValue(id, previous);
  const arrow = delta.change > 0 ? "up" : delta.change < 0 ? "down" : "minus";
  return (
    <span className="g-report-delta" data-tone={delta.tone} title={t("reports.delta.title", { value: formatKpi(locale, unit, before) })}>
      <Icon name={arrow} />
      {deltaText(t, delta, unit)}
      <span className="g-visually-hidden">{deltaHiddenText(t, locale, unit, before)}</span>
    </span>
  );
}
